import Styles from './ConnectAccount.module.scss'
import { Button } from 'blaze-ui/Button'
import { UserProfileAdd2 } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { SocialMediaIntegrationSelectionModal } from './SocialMediaIntegrationSelectionModal'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useFormikContext } from 'formik'
import { ContentCount } from 'types'
import {
  DEFAULT_INSTAGRAM_CONTENTS,
  DEFAULT_FACEBOOK_CONTENTS,
  DEFAULT_LINKEDIN_CONTENTS,
  DEFAULT_TWITTER_CONTENTS,
  DEFAULT_TIKTOK_CONTENTS,
  DEFAULT_YOUTUBE_CONTENTS,
  DEFAULT_GOOGLE_BUSINESS_PROFILE_CONTENTS,
} from 'constants/contentCountDefaults'

/**
 * Button to add a new social media account integration.
 * Must be rendered inside a Formik component.
 * Automatically adds newly connected integrations to the form when crossposting is disabled.
 */
export const NewAccountButton = () => {
  const { openModal } = useModals()
  const formik = useFormikContext<any>()

  const handleIntegrationAdded = (integration: ExternalIntegration) => {
    if (!formik) return

    // Only add to individual fields if crossposting is off
    const crosspostFrequency = formik.values.crosspost_frequency
    if (crosspostFrequency && crosspostFrequency !== 'never') {
      // Crossposting is enabled, don't add to individual fields
      return
    }

    const provider = integration.provider

    // Map provider to field name and default types
    const fieldMapping: Record<string, { field: string; defaults: readonly ContentCount[] }> = {
      [ExternalIntegrationEnum.Instagram]: { field: 'instagram_contents', defaults: DEFAULT_INSTAGRAM_CONTENTS },
      [ExternalIntegrationEnum.Facebook]: { field: 'facebook_contents', defaults: DEFAULT_FACEBOOK_CONTENTS },
      [ExternalIntegrationEnum.LinkedIn]: { field: 'linkedin_contents', defaults: DEFAULT_LINKEDIN_CONTENTS },
      [ExternalIntegrationEnum.X]: { field: 'twitter_contents', defaults: DEFAULT_TWITTER_CONTENTS },
      [ExternalIntegrationEnum.TikTok]: { field: 'tiktok_contents', defaults: DEFAULT_TIKTOK_CONTENTS },
      [ExternalIntegrationEnum.YouTube]: { field: 'youtube_contents', defaults: DEFAULT_YOUTUBE_CONTENTS },
      [ExternalIntegrationEnum.GoogleBusinessProfile]: {
        field: 'google_my_business_contents',
        defaults: DEFAULT_GOOGLE_BUSINESS_PROFILE_CONTENTS,
      },
    }

    const mapping = fieldMapping[provider]
    if (mapping) {
      const currentValues: ContentCount[] = formik.values[mapping.field] || []

      const accountAlreadyExists = currentValues.some((content) => content.account_ids?.includes(integration.id))

      if (!accountAlreadyExists) {
        const updatedValues = [...currentValues]

        mapping.defaults.forEach((defaultItem) => {
          const existingIndex = updatedValues.findIndex(
            (content) =>
              content.type === defaultItem.type && (!content.account_ids || content.account_ids.length === 0),
          )

          if (existingIndex >= 0) {
            // Update existing entry to include the new account ID
            updatedValues[existingIndex] = {
              ...updatedValues[existingIndex],
              account_ids: [integration.id],
            }
          } else {
            // No existing entry without account_ids, add a new one
            updatedValues.push({
              type: defaultItem.type,
              count: 0,
              account_ids: [integration.id],
            })
          }
        })

        formik.setFieldValue(mapping.field, updatedValues)
      }
    }
  }

  return (
    <div className={Styles.wrapper}>
      <Button
        onPress={() =>
          openModal(
            SocialMediaIntegrationSelectionModal,
            { onIntegrationAdded: handleIntegrationAdded },
            { newStack: true },
          )
        }
        color="var(---dark-80)"
        variant="secondary"
        size="md"
        frontIcon={UserProfileAdd2}
      >
        New Account
      </Button>
    </div>
  )
}
